<template>
  <div class="emoji-editor">
    <tinymce
      :id="editorId"
      v-model="content"
      :toolbar1="computedToolbar1"
      class="tinymce-editor"
      :other_options="tinymceOptions"
      :lazy="lazy"
    />

    <!-- 이모지 picker: 트리거 버튼은 TinyMCE 툴바의 'emoji' 커스텀 버튼으로 통합 -->
    <div v-if="showEmojiPicker" v-click-outside="closeEmojiPicker" class="emoji-picker-float">
      <Picker
        :native="true"
        :show-preview="false"
        :show-skin-tones="false"
        color="#008C95"
        @select="insertEmoji"
      />
    </div>

    <!-- 내부 링크 선택 모달: enableInternalLink 가 true 일 때만 렌더 -->
    <b-modal
      v-if="enableInternalLink"
      :id="internalLinkModalId"
      title="웹 보고서 내부 링크 선택"
      size="xl"
      dialog-class="internal-link-modal-wider"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      no-enforce-focus
      @hidden="onInternalLinkModalHidden"
    >
      <InternalLinkSelector
        v-if="internalLinkModalShown"
        :selected-url="internalLinkInitial.link"
        :selected-title="internalLinkInitial.title"
        :source-context="resolvedInternalLinkSourceContext"
        @link-selected="handleInternalLinkSelected"
        @close="$bvModal.hide(internalLinkModalId)"
      />
    </b-modal>

    <div v-if="isUploading" class="upload-overlay">
      <b-spinner variant="primary" />
      <div class="upload-progress-text">
        {{
          uploadProgress.total > 1
            ? `${uploadProgress.current} / ${uploadProgress.total} 업로드 중...`
            : '업로드 중...'
        }}
      </div>
    </div>

    <div v-if="showPastePreview" class="paste-preview-overlay" @click.self="cancelPasteHtml">
      <div class="paste-preview-modal">
        <h4 class="paste-preview-title">붙여넣기 미리보기</h4>
        <p class="paste-preview-desc">
          붙여넣으려는 내용이 HTML 코드입니다. 렌더링된 결과를 삽입할까요?
        </p>
        <div class="paste-preview-content" v-html="sanitizedPreview"></div>
        <div class="paste-preview-actions">
          <button class="paste-btn paste-btn-cancel" @click="cancelPasteHtml">아니오</button>
          <button class="paste-btn paste-btn-confirm" @click="confirmPasteHtml">예</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import 'emoji-mart-vue/css/emoji-mart.css';
import tinymce from 'vue-tinymce-editor';
import { Picker } from 'emoji-mart-vue';
import ClickOutside from '@/directives/click-outside';
import { generateRandomString } from '@/components/util';
import { sanitizeHtml } from '@/components/sanitize';
import debounce from 'lodash.debounce';
import InternalLinkSelector from '@/components/writing_report/story_board/main_components/InternalLinkSelector.vue';
import { releaseBodyScrollLockIfStandalone } from '@/utils/modal-scroll';
import {
  MAX_IMAGE_SIZE,
  MAX_IMAGE_COUNT,
  estimateBase64Size,
  partitionBySize,
  formatOversizedMessage
} from '@/utils/image-upload';

// heading 태그별 CSS font-size (rem) — content_style과 폰트 크기 표시에서 공유
const HEADING_FONT_SIZE_REM = {
  H1: 2.5,
  H2: 2,
  H3: 1.75,
  H4: 1.5,
  H5: 1.25,
  H6: 1.1
};

export default {
  name: 'CustomVueEditor',
  components: {
    tinymce,
    Picker,
    InternalLinkSelector
  },
  directives: {
    'click-outside': ClickOutside
  },
  props: {
    value: { type: String, default: '' },
    emoji: { type: Boolean, default: true },
    placeholder: { type: String, default: '지표에 대해 보고할 내용을 직접 작성합니다.' },
    height: { type: String, default: '300' },
    lazy: { type: Boolean, default: true },
    showTemplateButton: { type: Boolean, default: false },
    templateActive: { type: Boolean, default: false },
    enableInternalLink: { type: Boolean, default: false },
    internalLinkSourceContext: { type: Object, default: null }
  },
  data() {
    return {
      original_content: this.value || '',
      content: '',
      editor: null,
      showEmojiPicker: false,
      emojiBookmark: null,
      showPastePreview: false,
      pastedHtmlContent: '',
      internalLinkModalShown: false,
      internalLinkBookmark: null,
      internalLinkInitial: { link: '', title: '' },
      isUploading: false,
      uploadProgress: { current: 0, total: 0 }
    };
  },
  computed: {
    editorId() {
      return generateRandomString(5);
    },
    reportFontFamily() {
      return this.$store?.getters['report_list/selected_report']?.font_family || 'Pretendard';
    },
    reportFontSize() {
      return Number(this.$store?.getters['report_list/selected_report']?.font_size) || 16;
    },
    tablePadding() {
      const vPad = this.$store?.getters['report_contents/table_vertical_padding'] || '0.25';
      const hPad = this.$store?.getters['report_contents/table_horizontal_padding'] || '0.75';
      return `${vPad}rem ${hPad}rem`;
    },
    computedToolbar1() {
      const linkSegment = this.enableInternalLink ? 'link internallink' : 'link';
      let toolbar =
        'formatselect customfontsize | bold italic  strikethrough  forecolor backcolor | ' +
        linkSegment +
        ' | alignleft aligncenter alignright alignjustify  | numlist bullist outdent indent  | superscript subscript | hr | removeformat | annotation keymetric quotation';
      if (this.showTemplateButton) {
        toolbar += ' | designtemplate';
      }
      return toolbar;
    },
    computedToolbar2() {
      // emoji는 charmap(특수문자) 옆에 두어 '삽입' 계열로 묶임 — toolbar1 끝에 두면 줄바꿈으로 고립됨
      const emojiSegment = this.emoji ? ' emoji' : '';
      return `insertdatetime | charmap${emojiSegment} | fullscreen | code codesample`;
    },
    internalLinkModalId() {
      return `internal-link-modal-${this.editorId}`;
    },
    resolvedInternalLinkSourceContext() {
      if (this.internalLinkSourceContext) return this.internalLinkSourceContext;
      return {
        section: '본문 에디터',
        item_label: this.internalLinkInitial.title || '새 링크'
      };
    },
    sanitizedPreview() {
      return sanitizeHtml(this.pastedHtmlContent);
    },
    tinymceOptions() {
      return {
        debouncedEmit: null,
        relative_urls: false,
        remove_script_host: false,
        convert_urls: false,
        file_picker_types: 'image',
        file_picker_callback: (callback, value, meta) => {
          if (meta.filetype !== 'image') return;
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('multiple', '');
          input.setAttribute('accept', 'image/jpeg,image/png,image/webp,image/svg+xml');
          input.onchange = async () => {
            const files = Array.from(input.files || []);
            if (files.length === 0) return;

            if (files.length > MAX_IMAGE_COUNT) {
              this.$EventBus.$emit(
                'make-toast',
                '업로드 제한',
                `한 번에 최대 ${MAX_IMAGE_COUNT}장까지 업로드 가능합니다. (선택: ${files.length}장)`
              );
              return;
            }

            const { valid, invalid } = partitionBySize(files);
            if (invalid.length > 0) {
              this.$EventBus.$emit(
                'make-toast',
                '이미지 업로드 실패',
                formatOversizedMessage(invalid)
              );
            }
            if (valid.length === 0) return;

            this.isUploading = true;
            this.uploadProgress = { current: 0, total: valid.length };
            const failed = [];
            try {
              for (const [idx, file] of valid.entries()) {
                this.uploadProgress.current = idx + 1;
                const base64 = await this.fileToBase64(file);
                const id = 'blobid' + new Date().getTime() + '_' + idx;
                const blobCache = this.editor.editorUpload.blobCache;
                const blobInfo = blobCache.create(id, file, base64.split(',')[1]);
                blobCache.add(blobInfo);
                const uploadedUrl = await this.upload_image(blobInfo);
                if (!uploadedUrl) {
                  failed.push(file.name);
                  continue;
                }
                if (idx === 0) {
                  // 첫 파일: TinyMCE 이미지 다이얼로그 흐름 유지 (속성/제목 편집 가능)
                  callback(uploadedUrl, { title: file.name });
                } else {
                  // 나머지: 본문에 직접 삽입
                  const safeAlt = String(file.name).replace(/"/g, '&quot;');
                  this.editor.insertContent(`<img src="${uploadedUrl}" alt="${safeAlt}">`);
                }
              }
            } finally {
              this.isUploading = false;
            }

            if (failed.length > 0) {
              const list = failed.map(n => `• ${n}`).join('\n');
              this.$EventBus.$emit(
                'make-toast',
                '이미지 업로드 실패',
                `다음 파일 업로드에 실패했습니다:\n${list}`
              );
            }
          };

          input.click();
        },

        plugins: [
          'advlist autolink lists link image charmap print preview hr anchor pagebreak',
          'searchreplace wordcount visualblocks visualchars code fullscreen',
          'insertdatetime media nonbreaking save table contextmenu directionality',
          'paste textcolor colorpicker textpattern imagetools toc codesample'
        ],
        toolbar2: this.computedToolbar2,
        // menubar: 'file edit view insert format tools table',
        menubar: 'edit view insert format table',
        // 폰트 선택 목록
        font_family_formats:
          'Pretendard=Pretendard, sans-serif;' +
          'Noto Sans KR=Noto Sans KR, sans-serif;' +
          'Gothic A1=Gothic A1, sans-serif;' +
          'Nanum Myeongjo=Nanum Myeongjo, serif;' +
          'Nanum Gothic=Nanum Gothic, sans-serif;' +
          'Outfit=Outfit, sans-serif',
        paste_data_images: true,
        // images_upload_handler: this.upload_image, // 이미지 업로드 핸들러 비활성화 (중요)
        images_upload_handler: null, // 또는 제거
        automatic_uploads: false,

        // ✅ 이미지 대화상자 설정
        image_title: true,
        image_dimensions: true,
        image_uploadtab: false, // 핵심 설정!

        contextmenu:
          'alignElementLeft alignElementCenter alignElementRight | link image inserttable | cell row column deletetable',
        height: this.height,
        language: 'ko_KR',
        indent: false,
        remove_linebreaks: false, // 줄바꿈 유지
        force_br_newlines: false, // 엔터키 입력시 br 대신 p 태그 사용
        force_p_newlines: true, // 엔터키 입력시 p 태그 사용 강제
        forced_root_block: 'p', // 기본 블록 요소를 p로 설정
        language_url: '/etc/mce_ko_KR.js',
        images_file_types: 'jpeg,jpg,png,gif,svg,webp',

        // SVG 허용 설정
        verify_html: false, // HTML 검증 비활성화
        entity_encoding: 'raw', // 엔티티 인코딩 비활성화

        // SVG 태그와 속성을 명시적으로 허용
        extended_valid_elements:
          'svg[*],path[*],circle[*],rect[*],line[*],polyline[*],polygon[*],ellipse[*],g[*],defs[*],use[*],symbol[*],clipPath[*],mask[*],pattern[*],linearGradient[*],radialGradient[*],stop[*],text[*],tspan[*],textPath[*],foreignObject[*],image[*],animate[*],animateTransform[*],span[*],filter[*],feGaussianBlur[*],feOffset[*],feBlend[*],feFlood[*],feColorMatrix[*],feComposite[*],feMerge[*],feMergeNode[*],feImage[*],feMorphology[*],feDisplacementMap[*],feTurbulence[*],feConvolveMatrix[*],feDiffuseLighting[*],feSpecularLighting[*],fePointLight[*],feSpotLight[*],feDistantLight[*],title[*],desc[*],marker[*],metadata[*]',
        valid_children: '+body[svg],+div[svg],+p[svg],+span[svg]',
        content_style: `
          html { overflow-y: overlay; scrollbar-gutter: stable; }
          html::-webkit-scrollbar { width: 6px; }
          html::-webkit-scrollbar-track { background: transparent; }
          html::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 3px; }
          html::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.3); }
          body { font-family: '${this.reportFontFamily}', sans-serif !important; font-size: 1rem; color: #111111; line-height: 1.6; letter-spacing: -0.01px; }
          td, th { font-family: inherit !important; }
          p { margin-block-start: 0; margin-block-end: 1rem; text-align: justify; min-height: 1.6rem; }
          h1, h2, h3, h4, h5, h6 { line-height: 1.6; letter-spacing: -0.01px; }
          h1 { font-size: ${HEADING_FONT_SIZE_REM.H1}rem; font-weight: 700; margin-block-start: 0; margin-block-end: 0.5rem; }
          h2 { font-size: ${HEADING_FONT_SIZE_REM.H2}rem; font-weight: 700; margin-block-start: 0; margin-block-end: 0.5rem; }
          h3 { font-size: ${HEADING_FONT_SIZE_REM.H3}rem; font-weight: 600; margin-block-start: 0; margin-block-end: 0.5rem; }
          h4 { font-size: ${HEADING_FONT_SIZE_REM.H4}rem; font-weight: 600; margin-block-start: 0; margin-block-end: 0.5rem; }
          h5 { font-size: ${HEADING_FONT_SIZE_REM.H5}rem; font-weight: 600; margin-block-start: 0; margin-block-end: 0.5rem; }
          h6 { font-size: ${HEADING_FONT_SIZE_REM.H6}rem; font-weight: 600; margin-block-start: 0; margin-block-end: 0.5rem; }
          span.annotation { color: #6c757d; font-size: 0.875em; line-height: 1.4; }
          span.key-metric { font-weight: 700; color: #008C95; }
          blockquote { border-left: 3px solid #dee2e6; padding: 0.25rem 0 0.25rem 1rem; color: #6c757d; font-style: italic; margin: 0.5rem 0; }
          hr { border: 0; border-top: 1px solid rgba(0, 0, 0, 0.1); padding-bottom: 1rem; margin-top: 1rem; margin-bottom: 0; }
          pre[class*="language-"] { background: #f6f8fa; color: #24292e; font-family: 'Consolas', 'Monaco', 'Courier New', monospace; font-size: 0.85rem; line-height: 1.6; padding: 1rem 1.25rem; border-radius: 6px; border: 1px solid #e1e4e8; margin: 0.75rem 0; overflow-x: auto; white-space: pre; word-wrap: normal; }
          pre[class*="language-"] code { font-family: 'Consolas', 'Monaco', 'Courier New', monospace; font-size: inherit; color: inherit; background: none; padding: 0; }
          table { width: 100% !important; border-collapse: collapse; border-left: none !important; border-right: none !important; }
          table tr { height: 32px; }
          table > tbody > tr > td { padding: ${this.tablePadding}; border-bottom: 1px solid #dedede; font-weight: 400; font-size: 0.9rem; background-color: white; }
          table > tbody > tr > td > p { margin: 0 !important; font-weight: 400 !important; font-size: 0.9rem; min-height: auto; }
          table > tbody > tr > th, table > thead > tr > th, table > thead > tr > td { padding: ${this.tablePadding}; border-top: 1px solid #a2a2a2; border-bottom: 1px solid #a2a2a2; font-weight: 600; background-color: #efefef; color: #202020; font-size: 0.9rem; text-align: start; }
          table > tbody > tr > th > p, table > thead > tr > th > p, table > thead > tr > td > p { margin: 0 !important; font-weight: 600 !important; color: #202020; min-height: auto; text-align: start; }
          table > tbody > tr > td[data-mce-selected], table > tbody > tr > th[data-mce-selected], table > thead > tr > th[data-mce-selected], table > thead > tr > td[data-mce-selected] { background-color: #2276d2 !important; }
          ul, ol { padding-inline-start: 1rem !important; margin-block-start: 0; margin-block-end: 0; font-weight: 400; }
          ol { list-style-type: decimal; }
          ol ol { list-style-type: lower-alpha; }
          ol ol ol { list-style-type: lower-roman; }
          ol ol ol ol { list-style-type: decimal; }
          ol ol ol ol ol { list-style-type: lower-alpha; }
          ol ol ol ol ol ol { list-style-type: lower-roman; }
          ul { list-style-type: disc; }
          ul ul { list-style-type: circle; }
          ul ul ul { list-style-type: square; }
          ul ul ul ul { list-style-type: disc; }
          ul ul ul ul ul { list-style-type: circle; }
          ul ul ul ul ul ul { list-style-type: square; }
          ul li, ol li { font-weight: 400; }
          strong { font-weight: 600; }
          a { color: var(--teal, #008C95) !important; background-color: transparent !important; text-decoration: none; }
          img { max-width: 100%; height: auto; }
          mark { background-color: #fff3cd; color: #664d03; padding: 0.1em 0.2em; border-radius: 2px; }
          kbd { background-color: #212529; color: #fff; padding: 0.15em 0.4em; font-size: 0.85em; border-radius: 3px; font-family: 'Consolas', 'Monaco', 'Courier New', monospace; box-shadow: inset 0 -1px 0 rgba(0,0,0,0.25); }
        `,

        setup: editor => {
          this.editor = editor;

          // ── 커스텀 폰트 크기 선택 (직접 입력 지원) ──────────────────
          const fontSizes = [
            ...new Set([10, 12, 14, this.reportFontSize, 16, 18, 20, 24, 28, 36])
          ].sort((a, b) => a - b);
          const defaultFontSize = this.reportFontSize + 'px';
          const fontSizeItems = fontSizes.map(s => ({ text: s + 'px', value: s + 'px' }));
          fontSizeItems.push({ text: '직접 입력...', value: 'custom' });

          editor.addButton('customfontsize', function () {
            return {
              type: 'listbox',
              text: defaultFontSize,
              tooltip: 'Font Sizes',
              values: fontSizeItems,
              fixedWidth: true,
              onselect(e) {
                const val = e.control.settings.value;
                if (val === 'custom') {
                  editor.windowManager.open({
                    title: '폰트 크기 입력',
                    body: [
                      {
                        type: 'textbox',
                        name: 'size',
                        label: '크기 (px)'
                      }
                    ],
                    onsubmit(dlg) {
                      const size = parseInt(dlg.data.size, 10);
                      if (size >= 1 && size <= 200) {
                        editor.execCommand('FontSize', false, size + 'px');
                      }
                    }
                  });
                  return;
                }
                if (val) {
                  editor.execCommand('FontSize', false, val);
                }
              },
              onPostRender() {
                const ctrl = this;
                editor.on('NodeChange', () => {
                  const node = editor.selection.getNode();
                  if (node) {
                    // heading 노드인지 확인 (자기 자신 또는 부모)
                    const headingNode = node.closest && node.closest('h1,h2,h3,h4,h5,h6');
                    if (headingNode) {
                      const remVal = HEADING_FONT_SIZE_REM[headingNode.tagName];
                      if (remVal) {
                        const doc = editor.getDoc();
                        const rootFontSize =
                          parseFloat(
                            doc.defaultView.getComputedStyle(doc.documentElement).fontSize
                          ) || 16;
                        const px = Math.round(remVal * rootFontSize);
                        const pxStr = px + 'px';
                        ctrl.value(null);
                        ctrl.text(pxStr);
                        ctrl.disabled(true);
                        return;
                      }
                    }

                    ctrl.disabled(false);
                    const inlineSize = editor.dom.getStyle(node, 'font-size');
                    if (inlineSize) {
                      // 목록에 있는 사이즈면 value()로, 없으면 text()로 표시
                      if (fontSizeItems.some(item => item.value === inlineSize)) {
                        ctrl.value(inlineSize);
                      } else {
                        ctrl.value(null);
                        ctrl.text(inlineSize);
                      }
                    } else {
                      ctrl.value(defaultFontSize);
                    }
                  }
                });
              }
            };
          });

          // 주석 커스텀 버튼: 블록(p 등)을 annotation span으로 교체 (또는 복원)
          editor.addButton('annotation', {
            text: '주석',
            tooltip: '주석 스타일 적용/해제',
            onclick() {
              const node = editor.selection.getNode();

              // 현재 커서가 annotation span 안에 있으면 → p 태그로 복원
              const annotationSpan = editor.dom.getParent(
                node,
                el => el.tagName === 'SPAN' && editor.dom.hasClass(el, 'annotation')
              );
              if (annotationSpan) {
                const p = editor.dom.create('p');
                p.innerHTML = annotationSpan.innerHTML;
                editor.dom.replace(p, annotationSpan);
                editor.nodeChanged();
                editor.fire('change');
                return;
              }

              // 블록 부모(p, h1~h6)를 annotation span으로 교체
              const blockEl = editor.dom.getParent(node, el =>
                ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(el.tagName)
              );
              if (blockEl) {
                editor.undoManager.add();
                const span = editor.dom.create('span', {
                  class: 'annotation',
                  style: 'color: #6c757d; font-size: 87.5%; line-height: 1.4;'
                });
                span.innerHTML = blockEl.innerHTML;
                editor.dom.replace(span, blockEl);
                editor.nodeChanged();
                editor.fire('change');
                return;
              }

              // 블록 부모가 없는 경우(인라인 상태) 선택 영역에 인라인 스타일 적용
              editor.insertContent(
                `<span class="annotation" style="color: #6c757d; font-size: 87.5%; line-height: 1.4;">${
                  editor.selection.getContent() || ''
                }</span>`
              );
            },
            onPostRender() {
              const btn = this;
              editor.on('NodeChange', () => {
                const n = editor.selection.getNode();
                const inAnnotation = !!editor.dom.getParent(
                  n,
                  el => el.tagName === 'SPAN' && editor.dom.hasClass(el, 'annotation')
                );
                btn.active(inAnnotation);
              });
            }
          });

          // ── 블록 스타일 버튼 공통 팩토리 ──────────────────────────────
          // tag: 생성할 태그명, cls: 클래스명, style: 인라인 스타일
          // restoreTag: 해제 시 복원할 태그 (기본 'p')
          const addBlockStyleButton = (
            name,
            { text, tooltip, tag, cls, style, restoreTag = 'p' }
          ) => {
            editor.addButton(name, {
              text,
              tooltip,
              onclick() {
                const node = editor.selection.getNode();
                const existing = editor.dom.getParent(
                  node,
                  el => el.tagName === tag.toUpperCase() || (cls && editor.dom.hasClass(el, cls))
                );
                if (existing) {
                  const restored = editor.dom.create(restoreTag);
                  restored.innerHTML = existing.innerHTML;
                  editor.dom.replace(restored, existing);
                  editor.nodeChanged();
                  editor.fire('change');
                  return;
                }
                const blockEl = editor.dom.getParent(node, el =>
                  ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE'].includes(el.tagName)
                );
                if (blockEl) {
                  editor.undoManager.add();
                  const attrs = { style };
                  if (cls) attrs.class = cls;
                  const newEl = editor.dom.create(tag, attrs);
                  newEl.innerHTML = blockEl.innerHTML;
                  editor.dom.replace(newEl, blockEl);
                  editor.nodeChanged();
                  editor.fire('change');
                }
              },
              onPostRender() {
                const btn = this;
                editor.on('NodeChange', () => {
                  const n = editor.selection.getNode();
                  const active = !!editor.dom.getParent(
                    n,
                    el => el.tagName === tag.toUpperCase() || (cls && editor.dom.hasClass(el, cls))
                  );
                  btn.active(active);
                });
              }
            });
          };

          // 인용구
          addBlockStyleButton('quotation', {
            text: '인용구',
            tooltip: '인용구 적용/해제',
            tag: 'blockquote',
            cls: null,
            style:
              'border-left: 3px solid #dee2e6; padding: 0.25rem 0 0.25rem 1rem; color: #6c757d; font-style: italic; margin: 0.5rem 0;'
          });

          // 핵심 수치 (인라인)
          editor.addButton('keymetric', {
            text: '핵심수치',
            tooltip: '핵심 수치 강조 적용/해제',
            onclick() {
              const node = editor.selection.getNode();
              const existing = editor.dom.getParent(
                node,
                el => el.tagName === 'SPAN' && editor.dom.hasClass(el, 'key-metric')
              );
              if (existing) {
                editor.dom.remove(existing, true);
                editor.nodeChanged();
                editor.fire('change');
                return;
              }
              const selected = editor.selection.getContent();
              editor.insertContent(
                `<span class="key-metric" style="font-weight: 700; color: #008C95;">${selected}</span>`
              );
            },
            onPostRender() {
              const btn = this;
              editor.on('NodeChange', () => {
                const n = editor.selection.getNode();
                btn.active(
                  !!editor.dom.getParent(
                    n,
                    el => el.tagName === 'SPAN' && editor.dom.hasClass(el, 'key-metric')
                  )
                );
              });
            }
          });

          // 디자인 템플릿 버튼
          if (this.showTemplateButton) {
            const vueInstance = this;
            editor.addButton('designtemplate', {
              text: '템플릿',
              tooltip: '디자인 템플릿 패널 열기/닫기',
              onclick: () => {
                this.$emit('toggle-template');
              },
              onPostRender() {
                vueInstance._templateBtn = this;
              }
            });
          }

          // 내부 링크 버튼: 웹 보고서 내부 링크 선택 모달 열기
          if (this.enableInternalLink) {
            editor.addButton('internallink', {
              text: '내부링크',
              tooltip: '웹 보고서 내부 링크 삽입',
              onclick: () => {
                this.openInternalLinkModal();
              }
            });
          }

          // 이모티콘 버튼: 툴바에서 picker 열기
          // selection 북마크를 직접 저장해야 picker 클릭으로 포커스가 빠진 뒤에도 위치 복원 가능
          if (this.emoji) {
            editor.addButton('emoji', {
              text: '이모티콘',
              tooltip: '이모티콘 삽입',
              onclick: () => {
                this.emojiBookmark = editor.selection.getBookmark(2, true);
                this.toggleEmojiPicker();
              }
            });
          }

          // 에디터 포커스 이벤트 처리
          editor.on('focus', () => {
            this.$emit('editor-focus', true);
          });

          editor.on('Undo', () => {
            const content = editor.getContent({ format: 'text' }).trim();
            if (!content) {
              // 내용이 비었을 경우 초기값 복구
              editor.setContent(this.original_content);
            }
          });

          // ── 컨텍스트 메뉴 정렬 옵션 ──────────────────────────────
          // editor.dom.setStyle()을 사용하여 style과 data-mce-style을 동시에 동기화
          // (직접 DOM 조작 시 data-mce-style이 갱신되지 않아 저장 후 되돌아가는 문제 방지)
          const justifyMap = {
            left: 'flex-start',
            center: 'center',
            right: 'flex-end'
          };

          const setElementAlignment = align => {
            const node = editor.selection.getNode();
            if (!node || node === editor.getBody()) return;
            const justifyValue = justifyMap[align];

            // Case 1: 이미지 - flex wrapper 정렬
            if (node.tagName === 'IMG') {
              let wrapper = node.parentElement;
              if (!wrapper) return;
              if (wrapper.style.display !== 'flex') {
                // flex wrapper가 아직 없으면 생성
                wrapper = editor.dom.create('div', { style: 'display: flex' });
                node.parentElement.replaceChild(wrapper, node);
                wrapper.appendChild(node);
              }
              editor.dom.setStyle(wrapper, 'justify-content', justifyValue);
              editor.fire('change');
              return;
            }

            // Case 2: flex wrapper 내부 요소
            const flexParent = editor.dom.getParent(
              node,
              el => el.style && el.style.display === 'flex'
            );
            if (flexParent) {
              editor.dom.setStyle(flexParent, 'justify-content', justifyValue);
              editor.fire('change');
              return;
            }

            // Case 3: 블록 요소(table, hr 등) - flex wrapper로 감싸기
            const blockTags = ['TABLE', 'HR', 'IFRAME', 'SVG', 'FIGURE', 'VIDEO', 'AUDIO'];
            if (blockTags.includes(node.tagName) && node.parentElement) {
              const wrapper = editor.dom.create('div', { style: 'display: flex' });
              editor.dom.setStyle(wrapper, 'justify-content', justifyValue);
              node.parentElement.replaceChild(wrapper, node);
              wrapper.appendChild(node);
              editor.fire('change');
              return;
            }

            // Case 4: 텍스트/인라인 - text-align 적용
            const blockParent = editor.dom.getParent(node, 'p,div,td,th,li,blockquote');
            if (blockParent) {
              editor.dom.setStyle(blockParent, 'text-align', align);
              editor.fire('change');
            }
          };

          editor.addMenuItem('alignElementLeft', {
            text: '왼쪽 정렬',
            icon: 'alignleft',
            onclick: () => setElementAlignment('left')
          });
          editor.addMenuItem('alignElementCenter', {
            text: '가운데 정렬',
            icon: 'aligncenter',
            onclick: () => setElementAlignment('center')
          });
          editor.addMenuItem('alignElementRight', {
            text: '오른쪽 정렬',
            icon: 'alignright',
            onclick: () => setElementAlignment('right')
          });

          editor.on('NodeChange', e => {
            const body = editor.getBody();
            const imgs = body.querySelectorAll('img');

            imgs.forEach(img => {
              const parent = img.parentElement;
              if (!parent || parent.tagName === 'DIV') {
                const isFlex = parent?.style?.display === 'flex';
                if (!isFlex) {
                  const wrapper = document.createElement('div');
                  wrapper.style.display = 'flex';
                  parent?.replaceChild(wrapper, img);
                  wrapper.appendChild(img);
                }
              } else {
                const wrapper = document.createElement('div');
                wrapper.style.display = 'flex';
                parent.replaceChild(wrapper, img);
                wrapper.appendChild(img);
              }
            });
          });

          editor.on('blur', event => {
            // 이모지 선택을 위해 북마크 저장
            this.emojiBookmark = editor.selection.getBookmark(2, true);
            this.$emit('editor-focus', false);
            this.handleBlur(event);
          });

          editor.addShortcut('ctrl+s', 'Custom save', () => {
            this.$emit('save-content');
            setTimeout(this.hideTinyMCEError, 1);
          });

          // 모달 내부에서 TinyMCE 사용 시 플로팅 패널 위치 보정
          // body.modal-open 상태에서 CSS가 .mce-floatpanel을 position:fixed로 전환하므로,
          // TinyMCE가 계산한 absolute 좌표(뷰포트 + 스크롤)에서 스크롤 오프셋을 제거
          editor.on('paste', e => {
            const clipboardData = e.clipboardData || window.clipboardData;
            const pastedText = clipboardData.getData('text/plain') || clipboardData.getData('text');

            // HTML 코드로 판단되면 미리보기 모달 표시
            if (pastedText && /<[a-z][\s\S]*>/i.test(pastedText)) {
              e.preventDefault();
              e.stopPropagation();
              this.pastedHtmlContent = pastedText;
              this.showPastePreview = true;
            }
          });

          editor.on('init', () => {
            // iframe 내부에 프로젝트 폰트 로드
            const doc = editor.getDoc();
            ['/fonts/Pretendard/pretendard-subset.css', '/fonts/fonts.css'].forEach(href => {
              const link = doc.createElement('link');
              link.rel = 'stylesheet';
              link.href = href;
              doc.head.appendChild(link);
            });

            // iframe 내부에 report font-size 동적 적용
            const report = this.$store?.getters['report_list/selected_report'];
            if (report?.font_size) {
              doc.documentElement.style.fontSize = report.font_size + 'px';
            }

            // iframe 내부에 테이블 패딩 동적 적용
            this._mceDoc = doc;
            this._injectTablePaddingStyle();

            const editorEl = editor.getContainer();
            if (!editorEl || !editorEl.closest('.modal')) return;

            this._panelObserver = new MutationObserver(mutations => {
              for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                  if (node.nodeType !== 1) continue;
                  if (
                    node.classList.contains('mce-floatpanel') ||
                    node.classList.contains('mce-tooltip')
                  ) {
                    this._fixPanelPosition(node);
                  }
                }
              }
            });
            this._panelObserver.observe(document.body, { childList: true });
          });
        }
      };
    }
  },
  watch: {
    reportFontFamily(newFont) {
      if (this.editor && newFont) {
        const doc = this.editor.getDoc();
        if (doc && doc.body) {
          const safeFont = newFont.replace(/'/g, '');
          doc.body.style.fontFamily = `'${safeFont}', sans-serif`;
        }
      }
    },
    templateActive(val) {
      if (this._templateBtn) {
        this._templateBtn.active(val);
      }
    },
    async content(newVal) {
      if (this.lazy) {
        this.debouncedEmit(newVal);
      } else {
        this.$emit('input', await this.replaceBase64WithUrl(newVal));
      }
    },
    value(newVal) {
      // if (newVal !== this.content) {
      //   this.content = newVal;
      // }
    },
    tablePadding() {
      this._injectTablePaddingStyle();
    }
  },

  mounted() {
    this.$nextTick(() => {
      if (!this.value) {
        this.content = '';
      } else {
        this.content = this.value;
      }
    });
  },

  beforeDestroy() {
    if (this._panelObserver) {
      this._panelObserver.disconnect();
      this._panelObserver = null;
    }
    this._mceDoc = null;
    // 내부 링크 모달이 떠있는 상태에서 에디터가 언마운트될 경우 화면 스크롤 락 failsafe
    if (this.enableInternalLink && this.internalLinkModalShown) {
      releaseBodyScrollLockIfStandalone();
    }
  },

  created() {
    this.debouncedEmit = debounce(async val => {
      this.$emit('input', await this.replaceBase64WithUrl(val));
    }, 1000);
  },
  methods: {
    _injectTablePaddingStyle() {
      const doc = this._mceDoc;
      if (!doc) return;
      let styleEl = doc.getElementById('table-padding-override');
      if (!styleEl) {
        styleEl = doc.createElement('style');
        styleEl.id = 'table-padding-override';
        doc.head.appendChild(styleEl);
      }
      styleEl.textContent = `table td, table th { padding: ${this.tablePadding} !important; }`;
    },
    confirmPasteHtml() {
      if (this.editor && this.pastedHtmlContent) {
        this.editor.insertContent(sanitizeHtml(this.pastedHtmlContent));
      }
      this.showPastePreview = false;
      this.pastedHtmlContent = '';
    },
    cancelPasteHtml() {
      if (this.editor && this.pastedHtmlContent) {
        // HTML을 평문 텍스트로 이스케이프하여 삽입
        const escaped = this.pastedHtmlContent
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        this.editor.insertContent(escaped);
      }
      this.showPastePreview = false;
      this.pastedHtmlContent = '';
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    insertEmoji(emoji) {
      if (this.editor) {
        if (this.emojiBookmark) {
          this.editor.selection.moveToBookmark(this.emojiBookmark);
          this.emojiBookmark = null;
        }
        this.editor.insertContent(emoji.native);
        this.editor.focus();
      }
      this.showEmojiPicker = false;
    },
    closeEmojiPicker() {
      this.showEmojiPicker = false;
      this.emojiBookmark = null;
    },
    openInternalLinkModal() {
      const editor = this.editor;
      if (!editor) return;

      // 현재 커서가 <a> 안에 있으면 기존 링크 정보를 초기값으로
      const node = editor.selection.getNode();
      const linkEl = node && editor.dom.getParent(node, 'A');
      if (linkEl) {
        this.internalLinkInitial = {
          link: linkEl.getAttribute('href') || '',
          title: linkEl.textContent || ''
        };
        // 편집 시 <a> 전체를 선택해 두면 삽입이 자연스러움
        editor.selection.select(linkEl);
      } else {
        const selectedText = editor.selection.getContent({ format: 'text' }) || '';
        this.internalLinkInitial = { link: '', title: selectedText };
      }

      // TinyMCE iframe focus 가 빠진 후에도 위치 복원이 가능하도록 bookmark 저장
      this.internalLinkBookmark = editor.selection.getBookmark(2, true);

      this.internalLinkModalShown = true;
      this.$nextTick(() => {
        this.$bvModal.show(this.internalLinkModalId);
      });
    },
    handleInternalLinkSelected(linkData) {
      const editor = this.editor;
      const link = linkData && linkData.link ? String(linkData.link) : '';
      const title = linkData && linkData.title ? String(linkData.title) : link;

      if (!editor || !link) {
        this.$bvModal.hide(this.internalLinkModalId);
        return;
      }

      // bookmark 복원: 모달 오픈 중 사라진 selection 위치를 회복
      if (this.internalLinkBookmark) {
        try {
          editor.selection.moveToBookmark(this.internalLinkBookmark);
        } catch (e) {
          // bookmark 가 무효화되었을 경우 무시
        }
        this.internalLinkBookmark = null;
      }
      editor.focus();

      const safeHref = link
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const safeTitle = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const metaAttr = linkData && linkData.link_meta ? ' data-internal-link="true"' : '';

      // 현재 커서 위치의 <a> 가 있으면 갱신, 없으면 새 <a> 삽입
      // 내부 링크는 현재 창에서 이동시키기 위해 target/rel 을 명시적으로 제거
      // undoManager.transact 로 감싸 undo/redo 한 번에 동작하도록 함
      const node = editor.selection.getNode();
      const existing = node && editor.dom.getParent(node, 'A');
      const applyLink = () => {
        if (existing) {
          editor.dom.setAttribs(existing, {
            href: link,
            target: null,
            rel: null,
            'data-internal-link': linkData && linkData.link_meta ? 'true' : null
          });
          existing.textContent = title;
          editor.nodeChanged();
          editor.fire('change');
        } else {
          editor.execCommand(
            'mceInsertContent',
            false,
            `<a href="${safeHref}"${metaAttr}>${safeTitle}</a>`
          );
        }
      };
      if (editor.undoManager && typeof editor.undoManager.transact === 'function') {
        editor.undoManager.transact(applyLink);
      } else {
        applyLink();
      }

      this.$bvModal.hide(this.internalLinkModalId);
    },
    onInternalLinkModalHidden() {
      this.internalLinkModalShown = false;
      this.internalLinkInitial = { link: '', title: '' };
      this.internalLinkBookmark = null;
      // 모달이 닫힌 후 다른 모달이 떠있지 않다면 화면 스크롤 락 failsafe
      this.$nextTick(releaseBodyScrollLockIfStandalone);
      // TinyMCE 로 포커스 복귀
      if (this.editor) {
        this.$nextTick(() => {
          try {
            this.editor.focus();
          } catch (e) {
            // ignore
          }
        });
      }
    },
    async handleBlur(event) {
      const replaced = await this.replaceBase64WithUrl(this.content);
      this.$emit('input', replaced);
      this.$emit('blur', event);
    },
    hideTinyMCEError() {
      const errorAlert = document.querySelector('.mce-notification-error');
      if (errorAlert) {
        errorAlert.style.display = 'none';
      }
    },
    _fixPanelPosition(panel) {
      // TinyMCE는 position:absolute 기준으로 top = viewport_y + scrollY 를 설정함.
      // CSS에서 position:fixed로 전환했으므로 scrollY를 빼서 뷰포트 좌표로 보정.
      const fix = () => {
        if (!document.body.classList.contains('modal-open')) return;
        const top = parseFloat(panel.style.top) || 0;
        const left = parseFloat(panel.style.left) || 0;
        const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft || 0;
        if (scrollY > 0 || scrollX > 0) {
          panel.style.top = top - scrollY + 'px';
          panel.style.left = left - scrollX + 'px';
        }
      };
      // TinyMCE가 position을 설정한 뒤 보정하기 위해 한 프레임 대기
      requestAnimationFrame(fix);
    },
    async upload_image(blobInfo) {
      try {
        let base64;

        if (typeof blobInfo === 'string' && blobInfo.startsWith('data:image/')) {
          // 이미 base64 문자열인 경우 (replaceBase64WithUrl에서 호출)
          base64 = blobInfo;
        } else if (blobInfo?.blob) {
          // BlobInfo 객체인 경우 (images_upload_handler)
          const file = blobInfo.blob();
          base64 = await this.fileToBase64(file);
        } else {
          throw new Error('Invalid image input for upload_image');
        }

        const response = await axios.post(this.$store.state.backend_host + '/upload_file_to_s3', {
          image: base64,
          report_code: this.$store.state.report_list.report_code
        });

        if (response?.data?.url) {
          return response.data.url;
        } else {
          return null;
        }
      } catch (error) {
        this.$Sentry.captureException(error);
        this.$EventBus.$emit(
          'make-toast',
          '이미지 업로드 실패',
          '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
        );
        return null;
      }
    },

    async replaceBase64WithUrl(text) {
      /**
       * 문자열 내의 Base64 인코딩된 이미지를 찾아 upload_image()를 호출하여 업로드 후,
       * 해당 Base64 부분을 URL로 교체하는 함수.
       */

      if (typeof text !== 'string') {
        return text; // 문자열이 아니면 바로 리턴
      }
      const base64Pattern = /data:image\/[a-zA-Z]+;base64,([A-Za-z0-9+/=]+)/g;

      // 비동기 처리를 위해 모든 Base64 데이터를 찾고, 순차적으로 업로드 후 치환
      const matches = [...text.matchAll(base64Pattern)];
      if (matches.length === 0) return text;

      let oversizedCount = 0;
      this.isUploading = true;
      this.uploadProgress = { current: 0, total: matches.length };
      try {
        for (const [idx, match] of matches.entries()) {
          this.uploadProgress.current = idx + 1;
          const base64Data = match[0];

          // 붙여넣기/드롭으로 삽입된 base64 이미지의 size 사전 검증
          // split/join을 사용하여 동일 base64가 중복 등장해도 전부 치환되도록 처리
          if (estimateBase64Size(base64Data) > MAX_IMAGE_SIZE) {
            text = text.split(base64Data).join('');
            oversizedCount++;
            continue;
          }

          const uploadedUrl = await this.upload_image(base64Data);
          if (uploadedUrl) {
            text = text.split(base64Data).join(uploadedUrl);
          }
        }
      } finally {
        this.isUploading = false;
      }

      if (oversizedCount > 0) {
        this.$EventBus.$emit(
          'make-toast',
          '이미지 업로드 실패',
          `용량 초과 이미지 ${oversizedCount}개가 제거되었습니다. (각 10MB 이하만 가능)`
        );
      }

      return text;
    },
    async handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      try {
        // upload_image메소드가 base64기준으로 받고 있어서 일부러 한번 변환함
        const base64String = await this.fileToBase64(file);
        const url = await this.upload_image(base64String);
        Editor.insertEmbed(cursorLocation, 'image', url);
        resetUploader();
      } catch (error) {
        this.$Sentry.captureException(error);
      }
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    }
  }
};
</script>

<style>
.mce-container,
.mce-container *,
.mce-widget,
.mce-widget *,
.mce-reset {
  font-family: inherit !important;
}
.mce-ico {
  font-family: 'tinymce', Arial !important;
}
</style>

<style scoped>
.emoji-editor {
  position: relative;
}

.emoji-picker-float {
  position: fixed;
  bottom: 80px;
  right: 20px;
  z-index: 99999;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  overflow: hidden;
}

.rounded-editor {
  border-radius: 0.2rem;
  background-color: white;
}

.emoji-wrapper {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 1;
}

.emoji-button {
  border-radius: 0.25rem;
  padding: 5px 5px;
  font-size: 1.2em;
}

.ql-flip {
  z-index: 10 !important;
  left: 0 !important;
}

.upload-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  z-index: 50;
  pointer-events: all;
}

.upload-progress-text {
  font-size: 0.875rem;
  color: #495057;
  font-weight: 500;
}

.paste-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.paste-preview-modal {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  max-width: min(90vw, 1200px);
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.paste-preview-title {
  margin: 0 0 8px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #343a40;
}

.paste-preview-desc {
  margin: 0 0 16px;
  font-size: 0.875rem;
  color: #6c757d;
}

.paste-preview-content {
  flex: 1;
  overflow-y: auto;
  max-height: 60vh;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #f8f9fa;
}

.paste-preview-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.paste-btn {
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  border: 1px solid transparent;
}

.paste-btn-cancel {
  background: #fff;
  border-color: #dee2e6;
  color: #495057;
}

.paste-btn-cancel:hover {
  background: #f1f3f5;
}

.paste-btn-confirm {
  background: #008c95;
  color: #fff;
  border-color: #008c95;
}

.paste-btn-confirm:hover {
  background: #007a82;
}
</style>
